/**
 * Shadow commit: escreve em store shadow, valida, promove para active sem clear() no active.
 */

import type { QueueItem } from '../queue/types';
import {
  STORE_ITEMS,
  STORE_SHADOW,
  idbClearStore,
  idbDelete,
  idbGetAll,
  idbPut,
} from './idbQueueDb';
import {
  nextGeneration,
  readGenerationPointer,
  writeGenerationPointer,
} from './generationPointer';
import { computeQueueChecksum } from '../queueIntegrity';
import {
  recordStorageCommitLatency,
  recordShadowCommitFailed,
  recordGenerationSwapFailed,
} from '../telemetry';
import { getCoordinationEpoch } from '../coordination/coordinationEpoch';
import { getFlushLockFencingToken } from '../coordination/distributedLock';
import { getMinLeaderFencingToken } from '../tabCoordinator';
import {
  acquireOperationContext,
  assertOperationValid,
} from '../coordination/ownershipGate';
import { validateGenerationPointer } from './generationPointer';

export interface ShadowCommitResult {
  ok: boolean;
  generation: number;
  itemCount: number;
}

/**
 * Promove snapshot para active: shadow → merge → remove órfãos (sem store.clear no active).
 */
export async function commitQueueSnapshot(
  items: QueueItem[]
): Promise<ShadowCommitResult> {
  const ctx = acquireOperationContext('compaction');
  if (!assertOperationValid(ctx)) {
    recordShadowCommitFailed();
    return { ok: false, generation: 0, itemCount: 0 };
  }
  void getCoordinationEpoch();
  void getFlushLockFencingToken();
  void getMinLeaderFencingToken();

  const start = Date.now();
  const prevMeta = await readGenerationPointer();
  const generation = nextGeneration(prevMeta?.activeGeneration ?? 0);

  await idbClearStore(STORE_SHADOW);
  for (const item of items) {
    await idbPut(STORE_SHADOW, item);
  }

  const shadowItems = await idbGetAll(STORE_SHADOW);
  if (
    shadowItems.length !== items.length ||
    computeQueueChecksum(shadowItems) !== computeQueueChecksum(items)
  ) {
    recordStorageCommitLatency(Date.now() - start);
    recordShadowCommitFailed();
    return { ok: false, generation: prevMeta?.activeGeneration ?? 0, itemCount: 0 };
  }

  const newIds = new Set(items.map(i => i.id));
  const activeBefore = await idbGetAll(STORE_ITEMS);

  for (const item of items) {
    await idbPut(STORE_ITEMS, item);
  }
  for (const existing of activeBefore) {
    if (!newIds.has(existing.id)) {
      await idbDelete(STORE_ITEMS, existing.id);
    }
  }

  const meta = await writeGenerationPointer(items, generation);
  const activeAfter = await idbGetAll(STORE_ITEMS);
  if (!validateGenerationPointer(activeAfter, meta)) {
    recordGenerationSwapFailed();
    await idbClearStore(STORE_SHADOW);
    recordStorageCommitLatency(Date.now() - start);
    return { ok: false, generation: prevMeta?.activeGeneration ?? 0, itemCount: 0 };
  }
  await idbClearStore(STORE_SHADOW);

  recordStorageCommitLatency(Date.now() - start);
  return { ok: true, generation, itemCount: items.length };
}

export async function loadActiveQueueItems(): Promise<QueueItem[]> {
  return idbGetAll(STORE_ITEMS);
}
